import { type AttachmentEntityType } from "./attachments-store";
import { compressImage, compressOptionsForBucket } from "./image-compression";
import { buildFirmStoragePath, resolveStoragePathContext } from "./storage-paths";
import { dataProvider as supabase } from "@/lib/providers/data-provider";
import type { PortalType } from "@/lib/portal/portal-context-service";

const R2_PROXY_URL = (() => {
  const raw =
    typeof import.meta.env !== "undefined"
      ? (import.meta.env.VITE_R2_PROXY_URL as string | undefined)
      : undefined;
  if (!raw) return "https://storage.arivahly.tech";
  return raw.trim().replace(/[./]+$/, "").replace(/\/+$/, "") || "https://storage.arivahly.tech";
})();

async function r2AuthHeader(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session ? `Bearer ${data.session.access_token}` : "";
}

export function normalizeR2Url(url: string): string {
  if (!url) return "";
  return url
    .replace(/https?:\/\/[a-zA-Z0-9-_.]*arivahly\.workers\.dev/gi, R2_PROXY_URL)
    .replace(/https?:\/\/mtj-storage-proxy\.[a-zA-Z0-9-_.]+\.workers\.dev/gi, R2_PROXY_URL);
}

/** True when URL points at our authenticated Cloudflare R2 proxy. */
export function isR2ProxyUrl(url: string): boolean {
  if (!url) return false;
  try {
    const normalized = normalizeR2Url(url);
    const parsed = new URL(normalized);
    const host = parsed.hostname.toLowerCase();
    if (host === "r2.cloudflarestorage.com" || host.endsWith(".r2.cloudflarestorage.com")) return true;
    if (host.endsWith(".workers.dev") && host.includes("mtj-storage-proxy")) return true;
    if (R2_PROXY_URL) {
      const proxyHost = new URL(R2_PROXY_URL).hostname.toLowerCase();
      if (host === proxyHost) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// In-memory LRU/TTL cache & in-flight promise deduplication to minimize R2 latency and network roundtrips
const blobCache = new Map<string, { blob: Blob; ts: number }>();
const inFlightBlobFetches = new Map<string, Promise<Blob>>();
const BLOB_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes TTL

/**
 * Fetch image/file bytes for PDF embedding and rendering directly from Cloudflare R2 (Cached & Deduped).
 */
export async function fetchAuthorizedObjectBlob(url: string): Promise<Blob> {
  const trimmed = normalizeR2Url(url.trim());
  if (!trimmed) throw new Error("Empty image URL");

  // Check in-memory cache
  const cached = blobCache.get(trimmed);
  if (cached && Date.now() - cached.ts < BLOB_CACHE_TTL_MS) {
    return cached.blob;
  }

  // Check in-flight deduplication
  const existingFetch = inFlightBlobFetches.get(trimmed);
  if (existingFetch) {
    return existingFetch;
  }

  const fetchPromise = (async () => {
    try {
      if (trimmed.startsWith("data:")) {
        const res = await fetch(trimmed);
        if (!res.ok) throw new Error(`data URL fetch failed: ${res.status}`);
        const blob = await res.blob();
        blobCache.set(trimmed, { blob, ts: Date.now() });
        return blob;
      }

      if (trimmed.startsWith("blob:")) {
        const res = await fetch(trimmed);
        if (!res.ok) throw new Error(`blob URL fetch failed: ${res.status}`);
        const blob = await res.blob();
        blobCache.set(trimmed, { blob, ts: Date.now() });
        return blob;
      }

      let absolute = trimmed;
      if (trimmed.startsWith("/") && typeof window !== "undefined") {
        absolute = `${window.location.origin}${trimmed}`;
      }

      try {
        const res = await fetch(absolute, { mode: "cors" });
        if (res.ok) {
          const blob = await res.blob();
          blobCache.set(trimmed, { blob, ts: Date.now() });
          return blob;
        }
      } catch {
        /* proceed to fallback */
      }

      const auth = await r2AuthHeader();
      const res = await fetch(absolute, {
        headers: auth ? { Authorization: auth } : {},
      });
      if (!res.ok) throw new Error(`R2 image load failed: ${res.status}`);
      const blob = await res.blob();
      blobCache.set(trimmed, { blob, ts: Date.now() });
      return blob;
    } finally {
      inFlightBlobFetches.delete(trimmed);
    }
  })();

  inFlightBlobFetches.set(trimmed, fetchPromise);
  return fetchPromise;
}

/** Legacy R2 compatibility no-op. */
export function revokeR2DisplayUrl(bucket: string, path: string): void {
  /* direct R2 URLs require no revocation */
}

/**
 * Resolves a stable, high-performance browser-displayable URL for a Cloudflare R2 object.
 */
export function getDirectR2ObjectUrl(bucket: string, path: string): string {
  if (!path) return "";
  const normalized = normalizeR2Url(path);
  if (
    normalized.startsWith("data:") ||
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:")
  ) {
    return normalized;
  }
  const cleanPath = normalized.replace(/^\/+/, "");
  return `${R2_PROXY_URL}/${bucket}/${cleanPath}`;
}

/**
 * Compatibility alias pointing directly to R2 object URL.
 */
export function getSupabasePublicStorageUrl(bucket: string, path: string): string {
  return getDirectR2ObjectUrl(bucket, path);
}

/** Deletes an object from Cloudflare R2 and removes matching storage_file_metadata. */
export async function deleteFromSupabaseStorage(namespace: string, path: string): Promise<void> {
  const cleanPath = path.replace(/^\/+/, "");
  try {
    const auth = await r2AuthHeader();
    await fetch(`${R2_PROXY_URL}/${namespace}/${cleanPath}`, {
      method: "DELETE",
      headers: auth ? { Authorization: auth } : {},
    });
  } catch (err) {
    console.warn(`[storage] R2 delete warning for ${namespace}/${cleanPath}:`, err);
  }

  const { error: dbError } = await (supabase as any)
    .from("storage_file_metadata")
    .delete()
    .eq("bucket_id", namespace)
    .eq("storage_path", cleanPath);
  if (dbError) {
    console.warn("[storage] metadata delete failed:", dbError.message);
  }
}

/** Maps attachment entity types to the configured Cloudflare R2 namespace. */
export function getBucketForEntityType(
  entityType: AttachmentEntityType | "firm-logo" | "expense",
): string {
  if (entityType === "firm-logo") return "firm-assets";
  switch (entityType) {
    case "catalog":
      return "catalog-designs";
    case "person":
      return "customer-documents";
    case "worker":
      return "worker-kyc";
    case "supplier":
      return "supplier-documents";
    case "order":
    case "jobcard":
      return "order-attachments";
    case "repair":
      return "repair-attachments";
    case "expense":
      return "expense-receipts";
    case "stock":
      return "stock-assets";
    default:
      return "order-attachments";
  }
}

/** Resolves a stored object path to an accessible Cloudflare R2 URL. */
export async function resolveR2ObjectUrl(
  bucket: string,
  storagePath: string | null | undefined,
): Promise<string | null> {
  if (!storagePath) return null;
  return getAttachmentSignedUrl(bucket, storagePath);
}

let readyPromise: Promise<void> | null = null;

/** Compatibility no-op: Cloudflare R2 bucket is provisioned and managed globally. */
export function ensureStorageBucketsReady(): Promise<void> {
  if (!readyPromise) readyPromise = Promise.resolve();
  return readyPromise;
}

async function recordStorageMetadata(input: {
  context: { firmId: string; branchId: string };
  bucketId: string;
  path: string;
  entityId: string;
  mimeType: string;
  sizeBytes: number;
}): Promise<void> {
  const { data: auth } = await supabase.auth.getSession();
  const userId = auth?.session?.user?.id || "00000000-0000-0000-0000-000000000000";
  const { error } = await (supabase as any).from("storage_file_metadata").insert({
    firm_id: input.context.firmId,
    branch_id: input.context.branchId,
    entity_type: input.bucketId,
    entity_id: input.entityId,
    bucket_id: input.bucketId,
    storage_path: input.path,
    uploaded_by: userId,
    mime_type: input.mimeType,
    size_bytes: input.sizeBytes,
    visibility: "internal",
  });
  if (error) {
    console.warn(`[storage] Storage metadata record warning: ${error.message}`);
  }
}

export function base64ToBlob(dataUrl: string): { blob: Blob; mimeType: string } {
  const parts = dataUrl.split(";base64,");
  const mimeType = parts[0].split(":")[1] || "application/octet-stream";
  const characters = atob(parts[1]);
  const bytes = new Uint8Array(characters.length);
  for (let index = 0; index < characters.length; index += 1) {
    bytes[index] = characters.charCodeAt(index);
  }
  return { blob: new Blob([bytes.buffer], { type: mimeType }), mimeType };
}

/**
 * Stores a base64-encoded file directly into Cloudflare R2 via storage proxy.
 */
export async function uploadToSupabaseStorage(
  namespace: string,
  fileName: string,
  dataUrl: string,
  entityId: string,
  docKey: string,
): Promise<string> {
  const { blob, mimeType } = base64ToBlob(dataUrl);
  const safeName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_");
  const context = await resolveStoragePathContext();
  const path = buildFirmStoragePath(
    context,
    namespace,
    entityId,
    `${crypto.randomUUID()}-${safeName}`,
  );

  const auth = await r2AuthHeader();
  const cleanPath = path.replace(/^\/+/, "");
  const uploadRes = await fetch(`${R2_PROXY_URL}/${namespace}/${cleanPath}`, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType,
      ...(auth ? { Authorization: auth } : {}),
    },
    body: blob,
  });

  if (!uploadRes.ok) {
    const errText = await uploadRes.text().catch(() => "");
    throw new Error(`Cloudflare R2 storage upload failed (${uploadRes.status}): ${errText || uploadRes.statusText}`);
  }

  await recordStorageMetadata({
    context,
    bucketId: namespace,
    path,
    entityId,
    mimeType,
    sizeBytes: blob.size,
  });

  return path;
}

/**
 * Platform voice reference upload via Cloudflare R2.
 */
export async function uploadCentralOrnexaVoiceAudio(
  file: File | Blob,
  fileName = "ornexa-central-voice.webm",
): Promise<{ path: string; bucket: string; mimeType: string; sizeBytes: number }> {
  const blob = file instanceof Blob ? file : new Blob([file]);
  const mimeType = blob.type || "audio/webm";
  const safeName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_");
  const path = `platform/ornexa_central_voice/ref/${crypto.randomUUID()}-${safeName}`;
  
  const auth = await r2AuthHeader();
  const uploadRes = await fetch(`${R2_PROXY_URL}/firm-assets/${path}`, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType,
      ...(auth ? { Authorization: auth } : {}),
    },
    body: blob,
  });
  if (!uploadRes.ok) throw new Error(`Central voice upload to R2 failed: ${uploadRes.statusText}`);

  return { path, bucket: "firm-assets", mimeType, sizeBytes: blob.size };
}

/** Download central voice reference bytes via Cloudflare R2. */
export async function downloadCentralOrnexaVoiceAudio(
  bucket: string,
  path: string,
): Promise<Blob> {
  const cleanPath = path.replace(/^\/+/, "");
  const res = await fetch(`${R2_PROXY_URL}/${bucket}/${cleanPath}`);
  if (!res.ok) throw new Error(`Could not load central voice audio from R2: ${res.statusText}`);
  return res.blob();
}

/** Returns the canonical Cloudflare R2 URL for a stored file. */
export async function getAttachmentSignedUrl(
  namespace: string,
  path: string,
  forceRefresh = false,
): Promise<string> {
  if (!path) return "";
  const normalized = normalizeR2Url(path);
  if (
    normalized.startsWith("data:") ||
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:")
  ) {
    return normalized;
  }
  const cleanPath = normalized.replace(/^\/+/, "");
  return `${R2_PROXY_URL}/${namespace}/${cleanPath}`;
}

/** Compresses an image and stores it directly into Cloudflare R2 via storage proxy. */
export async function uploadFileToSupabase(
  namespace: string,
  file: File,
  entityId: string,
  docKey: string,
  branchId?: string | null,
  portalType?: PortalType | null,
): Promise<{ filePath: string; signedUrl: string }> {
  const { file: storedFile } = await compressImage(file, compressOptionsForBucket(namespace));
  const context = await resolveStoragePathContext(branchId, portalType);
  const filePath = buildFirmStoragePath(
    context,
    namespace,
    entityId,
    `${crypto.randomUUID()}-${storedFile.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`,
  );

  const auth = await r2AuthHeader();
  const cleanPath = filePath.replace(/^\/+/, "");
  const uploadRes = await fetch(`${R2_PROXY_URL}/${namespace}/${cleanPath}`, {
    method: "PUT",
    headers: {
      "Content-Type": storedFile.type || "application/octet-stream",
      ...(auth ? { Authorization: auth } : {}),
    },
    body: storedFile,
  });

  if (!uploadRes.ok) {
    const errText = await uploadRes.text().catch(() => "");
    throw new Error(`Cloudflare R2 storage upload failed (${uploadRes.status}): ${errText || uploadRes.statusText}`);
  }

  await recordStorageMetadata({
    context,
    bucketId: namespace,
    path: filePath,
    entityId,
    mimeType: storedFile.type || "application/octet-stream",
    sizeBytes: storedFile.size,
  });

  const signedUrl = getDirectR2ObjectUrl(namespace, filePath);
  return { filePath, signedUrl };
}
